// LT-312 — Cmd-K command palette. Built on @headlessui/react's
// `Dialog` + `Combobox` so focus trap + arrow-key nav + ESC-to-close
// come for free. Combobox's built-in options list handles the keyboard
// activeOption state; we just feed it a filtered list of matches.
//
// Implementation notes:
//  - Three groups: Recent, Navigation, Actions. Hidden groups when
//    empty. Recent is shown first when the input is empty (fastest
//    return-to-last), then Navigation, then Actions.
//  - Fuzzy match: case-insensitive substring on label + keywords. This
//    is simpler than a proper fuzzy lib (Fuse.js, fuse-jsx) and ships
//    ~zero bundle bytes; Fuse is ~10 KB gz and we can always upgrade if
//    users complain.
//  - `reduced-motion`: the Dialog's own transition honours the OS
//    prefers-reduced-motion setting via @headlessui's defaults.
//  - aria: Combobox provides role=listbox + role=option automatically;
//    Dialog provides role=dialog + aria-modal.
//  - Performance: <100ms open budget (AC). Matches cheap — under 30 nav
//    items + 6 actions + up to 10 recents. No server calls; no heavy
//    animation.
import React, { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { Search } from 'lucide-react';

import { useCommandPalette } from './CommandPaletteContext';
import {
  ACTIONS,
  NAV_ITEMS,
  labelForPath,
  readRecentPages,
  type ActionItem,
  type IconComponent,
  type NavItem,
  type RecentEntry,
} from './paletteSources';

type EntryKind = 'recent' | 'navigation' | 'action';

interface PaletteEntry {
  kind: EntryKind;
  id: string;
  label: string;
  to: string;
  icon: IconComponent;
  hint?: string;
}

function matches(haystack: string[], needle: string): boolean {
  if (!needle.trim()) return true;
  const n = needle.toLowerCase();
  return haystack.some((h) => h.toLowerCase().includes(n));
}

function navEntry(item: NavItem): PaletteEntry {
  return {
    kind: 'navigation',
    id: item.id,
    label: item.label,
    to: item.to,
    icon: item.icon,
  };
}

function actionEntry(item: ActionItem): PaletteEntry {
  return {
    kind: 'action',
    id: item.id,
    label: item.label,
    to: item.to,
    icon: item.icon,
  };
}

function recentEntry(recent: RecentEntry, icons: Map<string, IconComponent>): PaletteEntry {
  return {
    kind: 'recent',
    id: `recent:${recent.path}`,
    label: recent.label || labelForPath(recent.path),
    to: recent.path,
    icon: icons.get(recent.path) ?? Search,
    hint: recent.path,
  };
}

const CommandPalette: React.FC = () => {
  const { open, closePalette } = useCommandPalette();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  // Recompute when the palette opens so a just-visited page shows up
  // without waiting for a react-router-level notification.
  const recentEntries = useMemo<PaletteEntry[]>(() => {
    if (!open) return [];
    const navIconByPath = new Map<string, IconComponent>();
    NAV_ITEMS.forEach((n) => navIconByPath.set(n.to, n.icon));
    return readRecentPages().map((r) => recentEntry(r, navIconByPath));
  }, [open]);

  const filtered = useMemo<Record<EntryKind, PaletteEntry[]>>(() => {
    const recentMatches = recentEntries.filter((r) => matches([r.label, r.to], query));
    const navMatches = NAV_ITEMS
      .filter((n) => matches([n.label, ...(n.keywords ?? []), n.to], query))
      .map(navEntry);
    const actionMatches = ACTIONS
      .filter((a) => matches([a.label, ...(a.keywords ?? [])], query))
      .map(actionEntry);
    return {
      recent: recentMatches,
      navigation: navMatches,
      action: actionMatches,
    };
  }, [recentEntries, query]);

  const flatOptions = useMemo<PaletteEntry[]>(
    () => [...filtered.recent, ...filtered.navigation, ...filtered.action],
    [filtered],
  );

  const handleSelect = (entry: PaletteEntry | null) => {
    if (!entry) return;
    setQuery('');
    closePalette();
    navigate(entry.to);
  };

  const handleClose = () => {
    setQuery('');
    closePalette();
  };

  return (
    <Transition show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog
        onClose={handleClose}
        className="fixed inset-0 z-[60]"
        aria-label="Command palette"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden />
        </Transition.Child>

        <div className="fixed inset-0 flex items-start justify-center p-4 pt-[12vh]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-xl bg-surface-1 border border-white/[0.08] rounded-ds-xl shadow-ds-e3 overflow-hidden" data-testid="command-palette">
              <Combobox<PaletteEntry> onChange={handleSelect}>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.08]">
                  <Search className="w-4 h-4 text-slate-400" aria-hidden />
                  <Combobox.Input
                    autoFocus
                    data-command-palette-input="true"
                    placeholder="Search or jump to…"
                    className="flex-1 bg-transparent text-sm text-slate-50 placeholder:text-slate-500 outline-none"
                    onChange={(e) => setQuery(e.target.value)}
                    displayValue={() => query}
                  />
                  <span className="text-[10px] font-mono text-slate-500 border border-white/[0.08] rounded px-1.5 py-0.5">
                    ESC
                  </span>
                </div>

                <Combobox.Options
                  static
                  className="max-h-80 overflow-y-auto py-1"
                >
                  {flatOptions.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">
                      No matches for "{query}".
                    </div>
                  ) : (
                    <>
                      {filtered.recent.length > 0 ? (
                        <GroupSection title="Recent" entries={filtered.recent} />
                      ) : null}
                      {filtered.navigation.length > 0 ? (
                        <GroupSection title="Jump to" entries={filtered.navigation} />
                      ) : null}
                      {filtered.action.length > 0 ? (
                        <GroupSection title="Actions" entries={filtered.action} />
                      ) : null}
                    </>
                  )}
                </Combobox.Options>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

const GroupSection: React.FC<{ title: string; entries: PaletteEntry[] }> = ({
  title,
  entries,
}) => (
  <div className="py-1">
    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {title}
    </div>
    {entries.map((entry) => (
      <Combobox.Option
        key={entry.id}
        value={entry}
        as={Fragment}
      >
        {({ active }) => {
          const Icon = entry.icon;
          return (
            <div
              className={`flex items-center gap-3 px-3 py-2 mx-1 rounded-ds-md text-sm cursor-pointer transition-colors ${
                active
                  ? 'bg-accent-tint text-slate-50'
                  : 'text-slate-200 hover:bg-surface-2'
              }`}
              data-entry-id={entry.id}
            >
              <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-accent' : 'text-slate-400'}`} />
              <span className="flex-1 truncate">{entry.label}</span>
              {entry.hint ? (
                <span className="text-[11px] font-mono text-slate-500 truncate max-w-[40%]">
                  {entry.hint}
                </span>
              ) : null}
            </div>
          );
        }}
      </Combobox.Option>
    ))}
  </div>
);

export default CommandPalette;
